<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router'

const t2 = useT2({
  'Return policy': 'Grąžinimo politika',
  'I confirm that the returned items have not been used or otherwise damaged.': 'Patvirtinu, kad grąžinamos prekės nebuvo naudotos ar kitaip sugadintos.',
  'You must confirm that the returned items have not been used or otherwise damaged.': 'Turite patvirtinti, kad grąžinamos prekės nebuvo naudotos ar kitaip sugadintos.',

  'Gift card terms': 'Dovanų kortelės sąlygos',
  'I have read and agree to the': 'Susipažinau ir sutinku su',
  'gift card terms of use.': 'dovanų kortelės naudojimo sąlygomis.',
  'You must agree to the gift card terms of use.': 'Turite sutikti su dovanų kortelės naudojimo sąlygomis.',

  'Privacy policy': 'Privatumo politika',
  'I confirm that I have read the': 'Patvirtinu, kad susipažinau su',
  'privacy policy.': 'privatumo politika.',
  'You must confirm that you have read the privacy policy.': 'Turite patvirtinti, kad susipažinote su privatumo politika.',
  'The Privacy Policy explains how we process your personal data.': 'Privatumo politikoje paaiškinta, kaip tvarkome jūsų asmens duomenis.',

  'Purchase and return terms': 'Pirkimo ir grąžinimo sąlygos',
  'purchase and return terms.': 'pirkimo ir grąžinimo sąlygomis.',
  'You must agree to the purchase and return terms.': 'Turite sutikti su pirkimo ir grąžinimo sąlygomis.',

  Newsletter: 'Naujienlaiškis',
  'I want to receive newsletters and special offers by email.': 'Noriu el. paštu gauti naujienlaiškius ir specialius pasiūlymus.',
  'You can unsubscribe from the newsletter at any time.': 'Naujienlaiškio galite bet kada atsisakyti.',

  'Review reminders': 'Atsiliepimų priminimai',
  'I want to receive one email reminder after delivery to review the products I purchased.': 'Noriu po pristatymo el. paštu gauti vieną priminimą įvertinti įsigytas prekes.',
})

type AgreeType = 'gift' | 'privacy' | 'return' | 'buy' | 'newsletter' | 'reviewReminder'

const { linkPrivacy, linkTerms } = useLinks()
const { seller } = useSeller('browser')

const texts: Record<
  AgreeType,
  () => {
    title: stringI18n
    txt: stringI18n
    txtAfter?: stringI18n
    err?: stringI18n
    explain?: stringI18n
    documentId: () => string | None
    linkTxt?: stringI18n
    link?: RouteLocationRaw
  }
> = {
  return: () => ({
    title: t2('Return policy'),
    txt: t2('I confirm that the returned items have not been used or otherwise damaged.'),
    err: t2('You must confirm that the returned items have not been used or otherwise damaged.'),
    documentId: () => seller.value?.doc?.returns?._id,
  }),

  gift: () => ({
    title: t2('Gift card terms'),
    txt: t2('I have read and agree to the'),
    err: t2('You must agree to the gift card terms of use.'),
    documentId: () => undefined,
    linkTxt: t2('gift card terms of use.'),
  }),

  privacy: () => ({
    title: t2('Privacy policy'),
    txt: t2('I confirm that I have read the'),
    err: t2('You must confirm that you have read the privacy policy.'),
    explain: t2('The Privacy Policy explains how we process your personal data.'),
    documentId: () => seller.value?.doc?.privacyPolicy?._id,
    linkTxt: t2('privacy policy.'),
    link: linkPrivacy(),
  }),

  buy: () => ({
    title: t2('Purchase and return terms'),
    txt: t2('I have read and agree to the'),
    err: t2('You must agree to the purchase and return terms.'),
    documentId: () => seller.value?.doc?.termsAndConditions?._id,
    linkTxt: t2('purchase and return terms.'),
    link: linkTerms(),
  }),

  newsletter: () => ({
    title: t2('Newsletter'),
    txt: t2('I want to receive newsletters and special offers by email.'),
    txtAfter: t2('You can unsubscribe from the newsletter at any time.'),
    documentId: () => undefined,
  }),

  reviewReminder: () => ({
    title: t2('Review reminders'),
    txt: t2('I want to receive one email reminder after delivery to review the products I purchased.'),
    documentId: () => undefined,
  }),
}

const props = defineProps<{ type: AgreeType; right?: boolean; button?: boolean; explain?: boolean; optional?: boolean }>()
const active = defineModel<boolean>({ required: false, default: false })
const ui = computed(() => texts[props.type]?.() ?? texts.privacy?.())
const documentId = computed(() => ui.value.documentId())

const myInput = useMyInput<boolean>({
  optional: () => props.optional,
  label: () => ui.value.txt,
  value: () => active.value,
  validate: () => (ui.value.err ? [{ isErr: (i) => !i, msg: ui.value.err }] : undefined),
})
defineExpose(myInput.expose())

function onClick() {
  active.value = !active.value
}
function onAccept() {
  active.value = true
}
</script>

<template>
  <InputAggreeShell v-bind="$attrs" :onClick :txt="ui.txt" :active :right :button :txtAfter="ui.txtAfter">
    <template v-if="ui.link && ui.linkTxt" #after="{ classLink }">
      <AuthLink :class="classLink" :txt="ui.linkTxt" :to="ui.link" :document-id :modal-title="ui.title" :onAccept />
    </template>
    <template v-if="explain && ui.explain" #default>
      <UiWhy>{{ ui.explain }}</UiWhy>
    </template>
  </InputAggreeShell>
</template>
